import { useDebounce } from "$/util/debounce";
import { useTypeahead } from "$/util/search";
import { Casino } from "@mui/icons-material";
import { Autocomplete, IconButton, TextField } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

function PromptField({
  name,
  label,
  value,
  onChange,
  onBlur,
  onRandom,
  textFieldProps,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(false);
  const [cachedCompletions, setCachedCompletions] = useState([]);
  const deferredPrompt = useDebounce(value, 200);

  const { data = [] } = useTypeahead(deferredPrompt);

  const completions = useMemo(() => {
    return data.map((c: string) => `${deferredPrompt} ${c}`.replace(/\s+/g, " "));
  }, [data, deferredPrompt]);

  // Cache completions if non-empty
  useEffect(() => {
    if (completions.length > 0) {
      setCachedCompletions(completions);
      setOpen(true);
    }
  }, [completions]);

  return (
    <Autocomplete
      {...rest}
      freeSolo
      options={cachedCompletions}
      ListboxProps={{
        sx: {
          maxHeight: 190,
          "& .MuiAutocomplete-option": { padding: "0.25rem 0.85rem", minHeight: 32 },
        },
      }}
      blurOnSelect
      onBlur={(e) => {
        if (onBlur) onBlur(e);
        setFocus(false);
        setOpen(false);
      }}
      value={value || ""}
      open={open && focus}
      onFocus={() => {
        setFocus(true);
        setOpen(true);
      }}
      onInputChange={(e, val) => {
        if (val.length === 0) {
          if (open) setOpen(false);
        } else if (e && !open) {
          // Subtle bug: the e in condition keeps it closed on init
          setOpen(true);
        }
        onChange(e, val);
      }}
      onClose={() => setOpen(false)}
      disableClearable
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          name={name}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              (e.target as HTMLInputElement).blur();
            }
          }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <IconButton
                aria-label="Generate Random Idea"
                onClick={onRandom}
                size="small"
                color="primary"
              >
                <Casino />
              </IconButton>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            maxLength: 120,
          }}
          label={label}
          placeholder="Skull and Roses"
          multiline
          maxRows={4}
        />
      )}
    />
  );
}

export default PromptField;
